import React, { useCallback, useEffect, useState } from 'react';
import Header from '../Header';
import api from '../services/apiService';
import { useNotification } from './NotificationProvider';
import { FaCheck, FaChevronDown, FaCopy, FaTimes } from 'react-icons/fa';
import '../scss/membershipPlayers.scss';

const paidTone = (paid, total) => {
  if (total < 1) {
    return 'is-empty';
  }
  if (paid >= total) {
    return 'is-complete';
  }
  if (paid < 1) {
    return 'is-none';
  }
  return 'is-partial';
};

const MembershipPlayers = () => {
  const teamId = sessionStorage.getItem('selected_team_id');
  const { showSuccess, showError } = useNotification();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seasonPaid, setSeasonPaid] = useState(0);
  const [seasonPossible, setSeasonPossible] = useState(0);
  const [months, setMonths] = useState([]);
  const [openMonth, setOpenMonth] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [pageTitle, setPageTitle] = useState('Monthly Subscription');
  const [setupReady, setSetupReady] = useState(true);
  const [setupNote, setSetupNote] = useState('');

  const loadPlayers = useCallback(async () => {
    if (!teamId) {
      setError('No team selected');
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get(`/get_membership_players.php?team_id=${teamId}`);
      const data = response.data;
      if (!data.success) {
        throw new Error(data.message || 'Could not load subscriptions');
      }
      const nextMonths = Array.isArray(data.months) ? data.months : [];
      if (data.page_title) {
        setPageTitle(data.page_title);
      }
      setSetupReady(data.setup_ready !== false);
      setSetupNote(data.setup_note || '');
      setMonths(nextMonths);
      setSeasonPaid(data.paid_count || 0);
      setSeasonPossible(data.player_count || 0);
      const current = data.year_month || nextMonths[0]?.year_month || null;
      setOpenMonth(current);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not load subscriptions');
    } finally {
      setIsLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    loadPlayers();
  }, [loadPlayers]);

  const toggleMonth = (yearMonth) => {
    setOpenMonth((current) => (current === yearMonth ? null : yearMonth));
  };

  const copyLink = async (monthKey, player) => {
    if (player.is_free || !player.payment_link) {
      return;
    }
    const url = player.payment_link;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (!ok) {
          throw new Error('Copy command failed');
        }
      }
      setCopiedKey(`${monthKey}-${player.player_id}`);
      showSuccess('Payment link copied');
      window.setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      showError('Could not copy the payment link');
    }
  };

  return (
    <div className="membership-players-container container">
      <Header />
      <h1 className="page-title">{pageTitle}</h1>

      {isLoading && <div className="loading">Loading subscriptions…</div>}

      {!isLoading && error && <div className="error">{error}</div>}

      {!isLoading && !error && (
        <>
          <div className="membership-summary">
            <p className={`membership-setup ${setupReady ? 'is-ready' : 'is-pending'}`}>
              {setupReady ? 'Set up' : 'Not set up'}
            </p>
            {!setupReady && setupNote ? (
              <p className="membership-setup-note">{setupNote}</p>
            ) : null}
            <p className="membership-season-count">
              <span className={paidTone(seasonPaid, seasonPossible)}>
                {seasonPaid}/{seasonPossible}
              </span>
              {' '}paid this season
            </p>
          </div>

          {months.length === 0 ? (
            <div className="error">No subscription months found</div>
          ) : (
            <div className="membership-accordion">
              {months.map((month) => {
                const isOpen = openMonth === month.year_month;
                const paid = month.paid_count || 0;
                const total = month.player_count || 0;
                const panelId = `membership-panel-${month.year_month}`;
                return (
                  <section
                    key={month.year_month}
                    className={`membership-accordion-item${isOpen ? ' is-open' : ''}`}
                  >
                    <button
                      type="button"
                      className="membership-accordion-handle"
                      onClick={() => toggleMonth(month.year_month)}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                    >
                      <span className="membership-accordion-month">{month.name}</span>
                      <span className="membership-accordion-meta">
                        <span className={`membership-count ${paidTone(paid, total)}`}>
                          {paid}/{total}
                        </span>
                        <FaChevronDown className="membership-accordion-chevron" aria-hidden="true" />
                      </span>
                    </button>
                    {isOpen && (
                      <div id={panelId} className="membership-accordion-panel">
                        {(month.players || []).length === 0 ? (
                          <div className="membership-empty">No players found for this team</div>
                        ) : (
                          <ul className="membership-list">
                            {(month.players || []).map((player) => (
                              <li
                                key={player.player_id}
                                className={`membership-row${player.is_free ? ' is-free' : ''}`}
                              >
                                <div className="membership-player">
                                  <span className="membership-name">
                                    {player.player_name}
                                    {player.is_free ? <span className="membership-free">Free</span> : null}
                                  </span>
                                  {player.parent_name ? (
                                    <span className="membership-parent">{player.parent_name}</span>
                                  ) : null}
                                </div>
                                <div className="membership-actions">
                                  {player.is_free ? null : (
                                    <span
                                      className={`membership-status ${player.paid ? 'is-paid' : 'is-unpaid'}`}
                                      title={player.paid ? 'Paid' : 'Not paid'}
                                      aria-label={player.paid ? 'Paid' : 'Not paid'}
                                    >
                                      {player.paid ? <FaCheck /> : <FaTimes />}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    className="membership-copy"
                                    onClick={() => copyLink(month.year_month, player)}
                                    disabled={Boolean(player.is_free)}
                                    aria-label={
                                      player.is_free
                                        ? `${player.player_name} is free`
                                        : `Copy payment link for ${player.player_name}`
                                    }
                                  >
                                    <FaCopy />
                                    <span>
                                      {copiedKey === `${month.year_month}-${player.player_id}`
                                        ? 'Copied'
                                        : 'Copy link'}
                                    </span>
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MembershipPlayers;
